import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Separator } from '../components/ui/separator';
import { toast } from 'sonner';
import {
  Settings, DollarSign, Calendar, Globe, Save, Loader2, RefreshCw
} from 'lucide-react';
import api from '../api';

const CURRENCIES = [
  { code: 'CAD', symbol: '$', name: 'Canadian Dollar' },
  { code: 'USD', symbol: '$', name: 'US Dollar' },
  { code: 'EUR', symbol: '€', name: 'Euro' },
  { code: 'GBP', symbol: '£', name: 'British Pound' },
  { code: 'AUD', symbol: '$', name: 'Australian Dollar' },
  { code: 'JPY', symbol: '¥', name: 'Japanese Yen' },
  { code: 'CNY', symbol: '¥', name: 'Chinese Yuan' },
  { code: 'INR', symbol: '₹', name: 'Indian Rupee' },
  { code: 'BRL', symbol: 'R$', name: 'Brazilian Real' },
  { code: 'MXN', symbol: '$', name: 'Mexican Peso' },
  { code: 'CHF', symbol: 'Fr', name: 'Swiss Franc' },
  { code: 'SEK', symbol: 'kr', name: 'Swedish Krona' },
  { code: 'NOK', symbol: 'kr', name: 'Norwegian Krone' },
  { code: 'DKK', symbol: 'kr', name: 'Danish Krone' }
];

const TIMEZONES = [
  'America/Toronto',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Vancouver',
  'Europe/London',
  'Europe/Paris',
  'Europe/Berlin',
  'Asia/Tokyo',
  'Asia/Shanghai',
  'Asia/Singapore',
  'Australia/Sydney',
  'Pacific/Auckland'
];

const DATE_FORMATS = [
  { value: 'MM/dd/yyyy', label: 'MM/DD/YYYY (01/15/2026)' },
  { value: 'dd/MM/yyyy', label: 'DD/MM/YYYY (15/01/2026)' },
  { value: 'yyyy-MM-dd', label: 'YYYY-MM-DD (2026-01-15)' },
  { value: 'MMM dd, yyyy', label: 'MMM DD, YYYY (Jan 15, 2026)' }
];

export const CRMSettingsPage = () => {
  const { hasRole } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  const [settings, setSettings] = useState({
    default_currency: 'CAD',
    currency_symbol: '$',
    currency_position: 'before',
    decimal_separator: '.',
    thousands_separator: ',',
    allow_anonymous_ideas: false,
    default_campaign_approver_role: 'marketing_manager',
    auto_sync_campaigns_to_calendar: true,
    email_notifications_enabled: true,
    date_format: 'MM/dd/yyyy',
    time_format: '12h',
    default_timezone: 'America/Toronto'
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const res = await api.get('/settings/crm');
      setSettings(prev => ({ ...prev, ...res.data }));
    } catch (error) {
      console.error('Failed to fetch settings:', error);
      // Use defaults if not found
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await api.put('/settings/crm', settings);
      toast.success('CRM settings saved successfully');
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleCurrencyChange = (currencyCode) => {
    const currency = CURRENCIES.find(c => c.code === currencyCode);
    setSettings(prev => ({
      ...prev,
      default_currency: currencyCode,
      currency_symbol: currency?.symbol || '$'
    }));
  };

  if (!hasRole(['admin'])) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            <Settings className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium">Admin Access Required</h3>
            <p className="text-muted-foreground">Only administrators can manage CRM settings.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="crm-settings-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Settings className="h-6 w-6" />
            CRM Settings
          </h1>
          <p className="text-muted-foreground">
            Configure global CRM settings for your organization
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={fetchSettings}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Reset
          </Button>
          <Button onClick={handleSave} disabled={saving} data-testid="save-settings-btn">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            Save Settings
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Currency Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="h-5 w-5" />
              Currency Settings
            </CardTitle>
            <CardDescription>Configure default currency for the CRM</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Default Currency</Label>
              <Select
                value={settings.default_currency}
                onValueChange={handleCurrencyChange}
              >
                <SelectTrigger data-testid="currency-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CURRENCIES.map((currency) => (
                    <SelectItem key={currency.code} value={currency.code}>
                      {currency.code} ({currency.symbol}) - {currency.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Currency Symbol</Label>
                <Input
                  value={settings.currency_symbol}
                  onChange={(e) => setSettings(prev => ({ ...prev, currency_symbol: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Symbol Position</Label>
                <Select
                  value={settings.currency_position}
                  onValueChange={(v) => setSettings(prev => ({ ...prev, currency_position: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="before">Before ({settings.currency_symbol}100)</SelectItem>
                    <SelectItem value="after">After (100{settings.currency_symbol})</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Decimal Separator</Label>
                <Select
                  value={settings.decimal_separator}
                  onValueChange={(v) => setSettings(prev => ({ ...prev, decimal_separator: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value=".">Period (.)</SelectItem>
                    <SelectItem value=",">Comma (,)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Thousands Separator</Label>
                <Select
                  value={settings.thousands_separator}
                  onValueChange={(v) => setSettings(prev => ({ ...prev, thousands_separator: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value=",">Comma (,)</SelectItem>
                    <SelectItem value=".">Period (.)</SelectItem>
                    <SelectItem value=" ">Space ( )</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="p-3 bg-slate-50 rounded-lg">
              <Label className="text-xs text-muted-foreground">Preview</Label>
              <p className="text-lg font-semibold mt-1">
                {settings.currency_position === 'before' 
                  ? `${settings.currency_symbol}1${settings.thousands_separator}234${settings.decimal_separator}56`
                  : `1${settings.thousands_separator}234${settings.decimal_separator}56${settings.currency_symbol}`
                }
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Date & Time Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Date & Time Settings
            </CardTitle>
            <CardDescription>Configure date and time formats</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Default Timezone</Label>
              <Select
                value={settings.default_timezone}
                onValueChange={(v) => setSettings(prev => ({ ...prev, default_timezone: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TIMEZONES.map((tz) => (
                    <SelectItem key={tz} value={tz}>{tz}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Date Format</Label>
              <Select
                value={settings.date_format}
                onValueChange={(v) => setSettings(prev => ({ ...prev, date_format: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DATE_FORMATS.map((format) => (
                    <SelectItem key={format.value} value={format.value}>{format.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Time Format</Label>
              <Select
                value={settings.time_format}
                onValueChange={(v) => setSettings(prev => ({ ...prev, time_format: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="12h">12-hour (2:30 PM)</SelectItem>
                  <SelectItem value="24h">24-hour (14:30)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Marketing Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Globe className="h-5 w-5" />
              Marketing Settings
            </CardTitle>
            <CardDescription>Configure marketing module behavior</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Label>Allow Anonymous Idea Submissions</Label>
                <p className="text-xs text-muted-foreground">
                  Let employees submit marketing ideas anonymously
                </p>
              </div>
              <Switch
                checked={settings.allow_anonymous_ideas}
                onCheckedChange={(v) => setSettings(prev => ({ ...prev, allow_anonymous_ideas: v }))}
              />
            </div>
            
            <Separator />
            
            <div className="flex items-center justify-between">
              <div>
                <Label>Auto-sync Campaigns to Calendar</Label>
                <p className="text-xs text-muted-foreground">
                  Automatically add campaign dates to content calendar
                </p>
              </div>
              <Switch
                checked={settings.auto_sync_campaigns_to_calendar}
                onCheckedChange={(v) => setSettings(prev => ({ ...prev, auto_sync_campaigns_to_calendar: v }))}
              />
            </div>
            
            <Separator />
            
            <div className="space-y-2">
              <Label>Default Campaign Approver Role</Label>
              <Select
                value={settings.default_campaign_approver_role}
                onValueChange={(v) => setSettings(prev => ({ ...prev, default_campaign_approver_role: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="marketing_manager">Marketing Manager</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="sales_manager">Sales Manager</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Notification Settings */}
        <Card>
          <CardHeader>
            <CardTitle>Notification Settings</CardTitle>
            <CardDescription>Configure system notifications</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Label>Email Notifications</Label>
                <p className="text-xs text-muted-foreground">
                  Send email notifications for important events
                </p>
              </div>
              <Switch
                checked={settings.email_notifications_enabled}
                onCheckedChange={(v) => setSettings(prev => ({ ...prev, email_notifications_enabled: v }))}
              />
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CRMSettingsPage;
